import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type AnchorHTMLAttributes,
  type PropsWithChildren,
} from 'react';

type LocationValue = { pathname: string; search: string };
type NavigateOptions = { replace?: boolean };

const RouterContext = createContext<{
  location: LocationValue;
  navigate: (to: string | number, options?: NavigateOptions) => void;
} | null>(null);

function currentLocation(): LocationValue {
  return { pathname: window.location.pathname, search: window.location.search };
}

export function BrowserRouter({ children }: PropsWithChildren) {
  const [location, setLocation] = useState(currentLocation);

  useEffect(() => {
    const update = () => setLocation(currentLocation());
    window.addEventListener('popstate', update);
    window.addEventListener('qui:navigate', update);
    return () => {
      window.removeEventListener('popstate', update);
      window.removeEventListener('qui:navigate', update);
    };
  }, []);

  const navigate = useCallback((to: string | number, options: NavigateOptions = {}) => {
    if (typeof to === 'number') {
      window.history.go(to);
      return;
    }
    const target = new URL(to, window.location.origin);
    if (target.origin !== window.location.origin) throw new Error('external_navigation_blocked');
    const method = options.replace ? 'replaceState' : 'pushState';
    window.history[method]({}, '', `${target.pathname}${target.search}${target.hash}`);
    window.dispatchEvent(new Event('qui:navigate'));
  }, []);

  const value = useMemo(() => ({ location, navigate }), [location, navigate]);
  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

export function useLocation() {
  const router = useContext(RouterContext);
  if (!router) throw new Error('BrowserRouter missing');
  return router.location;
}

export function useNavigate() {
  const router = useContext(RouterContext);
  if (!router) throw new Error('BrowserRouter missing');
  return router.navigate;
}

export function useSearchParams() {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const setParams = useCallback(
    (next: URLSearchParams) => navigate(`${pathname}?${next.toString()}`),
    [navigate, pathname],
  );
  return [params, setParams] as const;
}

export function Link({
  to,
  children,
  onClick,
  ...props
}: AnchorHTMLAttributes<HTMLAnchorElement> & { to: string }) {
  const navigate = useNavigate();
  return (
    <a
      {...props}
      href={to}
      onClick={(event) => {
        onClick?.(event);
        if (
          !event.defaultPrevented &&
          event.button === 0 &&
          !event.metaKey &&
          !event.ctrlKey &&
          !event.shiftKey &&
          !event.altKey
        ) {
          event.preventDefault();
          navigate(to);
        }
      }}
    >
      {children}
    </a>
  );
}

export function NavLink({
  to,
  end,
  active: activeOverride,
  className,
  ...props
}: Parameters<typeof Link>[0] & { end?: boolean; active?: boolean }) {
  const { pathname } = useLocation();
  const targetPath = new URL(to, window.location.origin).pathname;
  const autoActive = end ? pathname === targetPath : pathname.startsWith(targetPath);
  const active = activeOverride ?? autoActive;
  return (
    <Link
      {...props}
      to={to}
      className={`${className ?? ''}${active ? ' active' : ''}`.trim()}
    />
  );
}

export function Navigate({ to, replace = true }: { to: string; replace?: boolean }) {
  const navigate = useNavigate();
  useEffect(() => navigate(to, { replace }), [navigate, replace, to]);
  return null;
}
